import { useState, useCallback, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './useAuth';

/**
 * Custom hook for managing Firestore document operations
 * @param collectionName - Firestore collection name
 * @param documentId - Optional document ID (if not provided, will use current user ID)
 * @returns Object containing document data, loading state, and CRUD methods
 */
export const useFirestoreDoc = <T,>(collectionName: string, documentId?: string) => { // Added trailing comma
  const { currentUser } = useAuth();
  const [data, setData] = useState<T | null>(null); // No comma needed here as it's unambiguous
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const docId = documentId || currentUser?.uid; // Corrected: use currentUser
  
  // Fetch document data
  const fetchData = useCallback(async () => {
    if (!docId || !currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Ensure we're authenticated before accessing Firestore
      if (!currentUser.uid) {
        throw new Error("Authentication required");
      }

      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setData(docSnap.data() as T); // Cast to T
      } else {
        setData(null);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [collectionName, docId]);

  // Create or update document
  const saveData = useCallback(async (newData: Partial<T>, merge: boolean = true) => { // No comma needed here
    if (!docId || !currentUser) return false;
    
    try {
      // Ensure we're authenticated before accessing Firestore
      if (!currentUser.uid) {
        throw new Error("Authentication required");
      }
      
      const docRef = doc(db, collectionName, docId);
      await setDoc(docRef, newData, { merge });
      setData(prev => (merge ? { ...prev, ...newData } : newData) as T | null); // Cast result
      return true;
    } catch (err) {
      console.error('Error saving document:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  }, [collectionName, docId]);

  // Update specific document fields
  const updateData = useCallback(async (fields: Partial<T>) => { // No comma needed here
    if (!docId || !currentUser) return false;
    
    try {
      // Ensure we're authenticated before accessing Firestore
      if (!currentUser.uid) {
        throw new Error("Authentication required");
      }
      
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, fields);
      setData(prev => ({ ...prev, ...fields }) as T | null); // Cast result
      return true;
    } catch (err) {
      console.error('Error updating document:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return false;
    }
  }, [collectionName, docId]);

  // Load data on mount or when dependencies change
  useEffect(() => {
    // Only fetch data if we have a currentUser
    if (currentUser) {
      fetchData();
    } else {
      // Clear data if we don't have a user
      setData(null);
      setLoading(false);
    }
  }, [fetchData, currentUser]);

  return {
    data,
    loading,
    error,
    fetchData,
    saveData,
    updateData
  };
};
